/**
 * Theta Data REST service for options (and stock) fetching only.
 * Streaming is handled by New RTL; Theta is used for all REST: snapshots, historical, intraday, EOD, Greeks, trade quote.
 *
 * By default all Theta REST goes through `getApiUrl('/api/theta/proxy')`, which backendRouting sends to the remote
 * FastAPI host (REACT_APP_REMOTE_FLOW_API_URL, default http://127.0.0.1:5001) so the VPS runs Theta Terminal + main.py.
 * Set REACT_APP_THETA_USE_BACKEND_PROXY=false to call Theta Cloud or a local terminal URL instead (REACT_APP_THETA_API_BASE_URL).
 */

import { getApiUrl } from "../config/api";

const DEFAULT_BASE = "https://api.thetadata.us/v3";
/** Local Theta Terminal: port from config.toml (default 25503). API v3 required. */
const LOCAL_TERMINAL_BASE = "http://127.0.0.1:25503/v3";

function useBackendProxy(): boolean {
    const env =
        typeof process !== "undefined"
            ? process.env
            : ({} as NodeJS.ProcessEnv);
    const v = (env.REACT_APP_THETA_USE_BACKEND_PROXY || "")
        .trim()
        .toLowerCase();
    if (v === "false" || v === "0" || v === "no") return false;
    return true;
}

function getBaseUrl(): string {
    const env =
        typeof process !== "undefined"
            ? process.env
            : ({} as NodeJS.ProcessEnv);
    let base = (env.REACT_APP_THETA_API_BASE_URL || "").trim() || DEFAULT_BASE;
    if (base.includes("/v2") && !base.includes("/v3"))
        base = base.replace("/v2", "/v3");
    if (!base.endsWith("/v3")) base = base.replace(/\/?$/, "/v3");
    return base;
}

/** Build request URL: when proxy is enabled, use backend /api/theta/proxy?path=...&params; otherwise direct terminal/cloud URL. */
function buildThetaUrl(path: string, params: URLSearchParams): string {
    const cleanPath = path.replace(/^\//, "");
    if (useBackendProxy()) {
        const proxyParams = new URLSearchParams(params);
        proxyParams.set("path", cleanPath);
        return getApiUrl("/api/theta/proxy") + "?" + proxyParams.toString();
    }
    const base = getBaseUrl().replace(/\/$/, "");
    return `${base}/${cleanPath}?${params.toString()}`;
}

function getAuthHeader(): string | undefined {
    const env =
        typeof process !== "undefined"
            ? process.env
            : ({} as NodeJS.ProcessEnv);
    const user = (env.REACT_APP_THETA_USERNAME || "").trim();
    const pass = (env.REACT_APP_THETA_PASSWORD || "").trim();
    if (!user && !pass) return undefined;
    const credentials = btoa(`${user}:${pass}`);
    return `Basic ${credentials}`;
}

/** Check if Theta is usable: credentials, local terminal, or backend proxy. */
export function isThetaDataAvailable(): boolean {
    if (useBackendProxy()) return true;
    const auth = getAuthHeader();
    const base = getBaseUrl();
    return !!(auth || base === LOCAL_TERMINAL_BASE);
}

/** Strike in dollars -> Theta format (1/10th of a cent). E.g. 170 -> 170000, 450.5 -> 450500 */
export function strikeToThetaTenths(strikeDollars: number): number {
    return Math.round(strikeDollars * 1000);
}

/** Theta 1/10th cent -> dollars. E.g. 170000 -> 170 */
export function thetaTenthsToStrike(tenths: number): number {
    return tenths / 1000;
}

// --- Response types (align with API response arrays) ---

/** OHLC bar: [ms_of_day, open, high, low, close, volume, count, date] */
export interface ThetaOptionOHLCRow {
    ms_of_day: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    count: number;
    date: number; // YYYYMMDD
}

/** First-order Greeks row (from hist/option/greeks). */
export interface ThetaOptionGreeksRow {
    ms_of_day: number;
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    rho?: number;
    bid?: number;
    ask?: number;
    underlying_price?: number;
    date: number;
}

/** Second-order Greeks: [ms_of_day, bid, ask, gamma, vanna, charm, vomma, veta, implied_vol, iv_error, ms_of_day2, underlying_price, date] */
export interface ThetaOptionGreeksSecondOrderRow {
    ms_of_day: number;
    bid: number;
    ask: number;
    gamma: number;
    vanna: number;
    charm: number;
    vomma: number;
    veta: number;
    implied_vol: number;
    iv_error: number;
    ms_of_day2: number;
    underlying_price: number;
    date: number;
}

/** Third-order Greeks (speed, color, ultima, etc.). Field names follow Theta response header. */
export interface ThetaOptionGreeksThirdOrderRow {
    ms_of_day: number;
    date: number;
    speed?: number;
    color?: number;
    ultima?: number;
    [key: string]: number | undefined;
}

export interface ThetaApiResponse<T> {
    header?: { format?: string[]; error_type?: string };
    response?: T;
}

export interface ThetaHistoricalOptionsParams {
    root: string;
    startDate: string; // YYYYMMDD
    endDate: string; // YYYYMMDD
    intervalMs?: number; // e.g. 60000 = 1 min
    exp?: string; // YYYYMMDD expiration (required for single-contract endpoints)
    strike?: number; // dollars; converted to 1/10th cent for API
    right?: "C" | "P";
    rth?: boolean; // regular trading hours only (default true)
    useCsv?: boolean;
}

// --- Rate limiting (simple in-memory throttle) ---
const MIN_MS_BETWEEN_REQUESTS = 200;
let lastRequestTime = 0;

async function throttle(): Promise<void> {
    const now = Date.now();
    const elapsed = now - lastRequestTime;
    if (elapsed < MIN_MS_BETWEEN_REQUESTS) {
        await new Promise((r) =>
            setTimeout(r, MIN_MS_BETWEEN_REQUESTS - elapsed),
        );
    }
    lastRequestTime = Date.now();
}

async function thetaFetch<T>(url: string, useCsv = false): Promise<T> {
    await throttle();
    const headers: Record<string, string> = {
        Accept: useCsv ? "text/csv" : "application/json",
    };
    if (!useBackendProxy()) {
        const auth = getAuthHeader();
        if (auth) headers["Authorization"] = auth;
    }

    const longOp =
        url.includes("greeks/all") ||
        url.includes("/history/") ||
        url.includes("option/history");
    const abortMs = longOp ? 90_000 : 30_000;
    const abortCtrl = new AbortController();
    const timer = setTimeout(() => abortCtrl.abort(), abortMs);
    let res: Response;
    try {
        res = await fetch(url, { headers, signal: abortCtrl.signal });
    } finally {
        clearTimeout(timer);
    }
    if (!res.ok) {
        const text = await res.text();
        throw new Error(
            res.status === 401
                ? "Invalid credentials"
                : `Request failed: ${res.status}`,
        );
    }
    if (useCsv) return res.text() as unknown as T;
    return res.json() as Promise<T>;
}

/** v3 params: symbol, expiration, strike (float), right (call|put), interval (1m). */
function buildParams(
    params: ThetaHistoricalOptionsParams & { strikeTheta?: number },
): URLSearchParams {
    const p = new URLSearchParams();
    p.set("symbol", params.root);
    p.set("start_date", params.startDate);
    p.set("end_date", params.endDate);
    if (params.intervalMs != null && params.intervalMs > 0) {
        const min = Math.max(1, Math.round(params.intervalMs / 60000));
        if (min === 1) {
            p.set("interval", "1m");
        } else if (min <= 60) {
            p.set("interval", `${min}m`);
        } else {
            const hours = Math.round(min / 60);
            // Theta v3 option/history rejects 24h and day intervals; coarsest bar is 1h.
            p.set("interval", hours >= 24 ? "1h" : `${hours}h`);
        }
    } else {
        p.set("interval", "1m");
    }
    if (params.exp) p.set("expiration", params.exp);
    const strike =
        params.strike ??
        (params.strikeTheta != null ? params.strikeTheta / 1000 : undefined);
    if (strike != null) p.set("strike", String(strike));
    const right =
        params.right === "C"
            ? "call"
            : params.right === "P"
              ? "put"
              : params.right;
    if (right)
        p.set("right", String(right).toLowerCase() === "put" ? "put" : "call");
    if (params.useCsv) p.set("format", "csv");
    return p;
}

/**
 * Snapshot params for v3 option/snapshot/* (symbol, expiration, strike, right).
 * Expiration accepts YYYY-MM-DD or YYYYMMDD, or * for all expirations (greeks/all chain mode).
 */
function snapshotParams(
    root: string,
    exp: string,
    strike: number,
    right: "C" | "P",
) {
    const p = new URLSearchParams();
    p.set("symbol", root);
    if (exp === "*") {
        p.set("expiration", "*");
    } else {
        p.set("expiration", exp.replace(/-/g, "").slice(0, 8));
    }
    p.set("strike", String(strike));
    p.set("right", right === "P" ? "put" : "call");
    return p;
}

/** Params for greeks/all across all strikes and expirations for one underlying (v3: expiration=*, strike=*, right=both). */
export function snapshotGreeksAllChainParams(root: string): URLSearchParams {
    const p = new URLSearchParams();
    p.set("symbol", root);
    p.set("expiration", "*");
    p.set("strike", "*");
    p.set("right", "both");
    return p;
}

/** Last trade snapshot: price, size, timestamp. */
export interface ThetaOptionSnapshotTrade {
    price?: number;
    size?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Fetch option snapshot/trade (last trade for the contract today).
 */
export async function fetchThetaOptionSnapshotTrade(params: {
    root: string;
    exp: string;
    strike: number;
    right: "C" | "P";
}): Promise<ThetaOptionSnapshotTrade | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const url = buildThetaUrl("option/snapshot/trade", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    if (typeof item === "object" && item !== null && "data" in item) {
        const data = (item as { data?: Record<string, unknown> }).data;
        if (data && typeof data === "object") {
            const price = data.price ?? data.last ?? data.last_price;
            return {
                price:
                    typeof price === "number"
                        ? price
                        : typeof price === "string"
                          ? parseFloat(price)
                          : undefined,
                size:
                    typeof data.size === "number"
                        ? data.size
                        : typeof data.size === "string"
                          ? parseInt(data.size, 10)
                          : undefined,
                timestamp:
                    typeof data.timestamp === "string"
                        ? data.timestamp
                        : undefined,
                ...data,
            } as ThetaOptionSnapshotTrade;
        }
    }
    if (typeof item === "object" && item !== null) {
        const o = item as Record<string, unknown>;
        const price = o.price ?? o.last ?? o.last_price;
        return {
            price:
                typeof price === "number"
                    ? price
                    : typeof price === "string"
                      ? parseFloat(price)
                      : undefined,
            size: typeof o.size === "number" ? o.size : undefined,
            timestamp:
                typeof o.timestamp === "string" ? o.timestamp : undefined,
            ...o,
        } as ThetaOptionSnapshotTrade;
    }
    return null;
}

/** Snapshot market value (e.g. mark or theoretical value). */
export interface ThetaOptionSnapshotMarketValue {
    market_value?: number;
    value?: number;
    [key: string]: unknown;
}

/**
 * Fetch option snapshot/market_value.
 */
export async function fetchThetaOptionSnapshotMarketValue(params: {
    root: string;
    exp: string;
    strike: number;
    right: "C" | "P";
}): Promise<ThetaOptionSnapshotMarketValue | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const url = buildThetaUrl("option/snapshot/market_value", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    const data =
        typeof item === "object" && item !== null && "data" in item
            ? (item as { data?: Record<string, unknown> }).data
            : item;
    if (data && typeof data === "object") {
        const v =
            (data as Record<string, unknown>).market_value ??
            (data as Record<string, unknown>).value;
        return {
            market_value:
                typeof v === "number"
                    ? v
                    : typeof v === "string"
                      ? parseFloat(v)
                      : undefined,
            ...(data as object),
        } as ThetaOptionSnapshotMarketValue;
    }
    return null;
}

/** Snapshot implied volatility. */
export interface ThetaOptionSnapshotGreeksIV {
    implied_volatility?: number;
    iv?: number;
    [key: string]: unknown;
}

/**
 * Fetch option snapshot/greeks/implied_volatility.
 */
export async function fetchThetaOptionSnapshotGreeksImpliedVolatility(params: {
    root: string;
    exp: string;
    strike: number;
    right: "C" | "P";
}): Promise<ThetaOptionSnapshotGreeksIV | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const url = buildThetaUrl("option/snapshot/greeks/implied_volatility", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    const data =
        typeof item === "object" && item !== null && "data" in item
            ? (item as { data?: Record<string, unknown> }).data
            : item;
    if (data && typeof data === "object") {
        const v =
            (data as Record<string, unknown>).implied_volatility ??
            (data as Record<string, unknown>).iv;
        return {
            implied_volatility:
                typeof v === "number"
                    ? v
                    : typeof v === "string"
                      ? parseFloat(v)
                      : undefined,
            ...(data as object),
        } as ThetaOptionSnapshotGreeksIV;
    }
    return null;
}

/**
 * Fetch option/snapshot/greeks/all (v3 Pro): full greeks row for one contract, or use snapshotGreeksAllChainParams for chain-wide.
 */
export async function fetchThetaOptionSnapshotGreeksAll(params: {
    root: string;
    exp: string;
    strike: number;
    right: "C" | "P";
}): Promise<ThetaOptionGreeksRow | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const url = buildThetaUrl("option/snapshot/greeks/all", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    const data =
        typeof item === "object" && item !== null && "data" in item
            ? (item as { data?: Record<string, unknown> }).data
            : item;
    if (data && typeof data === "object") {
        const d = data as Record<string, unknown>;
        return {
            ms_of_day: typeof d.ms_of_day === "number" ? d.ms_of_day : 0,
            delta: typeof d.delta === "number" ? d.delta : undefined,
            gamma: typeof d.gamma === "number" ? d.gamma : undefined,
            theta: typeof d.theta === "number" ? d.theta : undefined,
            vega: typeof d.vega === "number" ? d.vega : undefined,
            rho: typeof d.rho === "number" ? d.rho : undefined,
            bid: typeof d.bid === "number" ? d.bid : undefined,
            ask: typeof d.ask === "number" ? d.ask : undefined,
            underlying_price:
                typeof d.underlying_price === "number"
                    ? d.underlying_price
                    : undefined,
            date: typeof d.date === "number" ? d.date : 0,
        } as ThetaOptionGreeksRow;
    }
    return null;
}

/**
 * Theta endpoints for flow execution side (NBBO + OPRA 145/146).
 * Always prefer `trade_quote` — `history/trade` and WS TRADE lack reliable bid/ask.
 */
export const THETA_FLOW_SIDE_ENDPOINTS = {
    /** Full: trade + NBBO + condition — use for side / aggressor */
    preferred: "option/history/trade_quote",
    /** Partial: condition 145/146 only; condition 18 needs NBBO */
    tradeOnly: "option/history/trade",
    /** NBBO snapshots only (no trade condition) */
    quoteOnly: "option/history/quote",
} as const;

/** One history trade bar (price, size, timestamp). */
export interface ThetaOptionHistoryTradeRow {
    price?: number;
    size?: number;
    timestamp?: string;
    bid?: number;
    ask?: number;
    condition?: number | string;
    execution_side?: string;
    composite_side?: string;
    [key: string]: unknown;
}

/** Alias kept for backward compatibility with hooks that reference the old name. */
export type ThetaFlowSideTradeRow = ThetaOptionHistoryTradeRow;

/**
 * Fetch option history/trade (intraday trade bars). Uses interval=1m; requires startDate, endDate.
 * Avoid for execution side — use fetchFlowSideAwareHistoryTrades (trade_quote first).
 */
export async function fetchThetaOptionHistoryTrade(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryTradeRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/trade", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryTradeRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    price: typeof r.price === "number" ? r.price : undefined,
                    size: typeof r.size === "number" ? r.size : undefined,
                    timestamp:
                        typeof r.timestamp === "string"
                            ? r.timestamp
                            : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/**
 * Side-aware history: trade_quote first (NBBO + OPRA 145/146), fallback to history/trade.
 */
export async function fetchFlowSideAwareHistoryTrades(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<{ rows: ThetaOptionHistoryTradeRow[]; endpoint: string }> {
    const tq = await fetchThetaOptionHistoryTradeQuote(params);
    if (tq.length > 0) {
        return { rows: tq, endpoint: THETA_FLOW_SIDE_ENDPOINTS.preferred };
    }
    const tr = await fetchThetaOptionHistoryTrade(params);
    return { rows: tr, endpoint: THETA_FLOW_SIDE_ENDPOINTS.tradeOnly };
}

/** One history quote bar (bid, ask, timestamp). */
export interface ThetaOptionHistoryQuoteRow {
    bid?: number;
    ask?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Fetch option history/quote (intraday quote snapshots). Uses interval=1m; requires startDate, endDate.
 */
export async function fetchThetaOptionHistoryQuote(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryQuoteRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/quote", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryQuoteRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    bid: typeof r.bid === "number" ? r.bid : undefined,
                    ask: typeof r.ask === "number" ? r.ask : undefined,
                    timestamp:
                        typeof r.timestamp === "string"
                            ? r.timestamp
                            : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/** One history trade_quote bar (trade + quote combined). */
export interface ThetaOptionHistoryTradeQuoteRow {
    price?: number;
    bid?: number;
    ask?: number;
    size?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Fetch option history/trade_quote (intraday trade+quote bars). Uses interval=1m.
 */
export async function fetchThetaOptionHistoryTradeQuote(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryTradeQuoteRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/trade_quote", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryTradeQuoteRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    price: typeof r.price === "number" ? r.price : undefined,
                    bid: typeof r.bid === "number" ? r.bid : undefined,
                    ask: typeof r.ask === "number" ? r.ask : undefined,
                    size: typeof r.size === "number" ? r.size : undefined,
                    timestamp:
                        typeof r.timestamp === "string"
                            ? r.timestamp
                            : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/** One history open_interest point. */
export interface ThetaOptionHistoryOpenInterestRow {
    open_interest?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Fetch option history/open_interest (OI time series).
 */
export async function fetchThetaOptionHistoryOpenInterest(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryOpenInterestRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/open_interest", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryOpenInterestRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    open_interest:
                        typeof r.open_interest === "number"
                            ? r.open_interest
                            : undefined,
                    timestamp:
                        typeof r.timestamp === "string"
                            ? r.timestamp
                            : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/** EOD Greeks bar (delta, gamma, theta, vega, etc. per day). */
export interface ThetaOptionHistoryGreeksEodRow {
    date?: number;
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    [key: string]: unknown;
}

/**
 * Fetch option history/greeks/eod (EOD Greeks).
 */
export async function fetchThetaOptionHistoryGreeksEod(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryGreeksEodRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/eod", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryGreeksEodRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    date: typeof r.date === "number" ? r.date : undefined,
                    delta: typeof r.delta === "number" ? r.delta : undefined,
                    gamma: typeof r.gamma === "number" ? r.gamma : undefined,
                    theta: typeof r.theta === "number" ? r.theta : undefined,
                    vega: typeof r.vega === "number" ? r.vega : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/** One history implied_volatility bar. */
export interface ThetaOptionHistoryGreeksImpliedVolatilityRow {
    implied_volatility?: number;
    iv?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Fetch option history/greeks/implied_volatility. Uses interval=1m.
 */
export async function fetchThetaOptionHistoryGreeksImpliedVolatility(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryGreeksImpliedVolatilityRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/implied_volatility", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(url);
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const out: ThetaOptionHistoryGreeksImpliedVolatilityRow[] = [];
    for (const item of response) {
        if (item == null || typeof item !== "object") continue;
        const data =
            "data" in item && Array.isArray((item as { data: unknown }).data)
                ? (item as { data: unknown[] }).data
                : "data" in item &&
                    typeof (item as { data: unknown }).data === "object"
                  ? [(item as { data: Record<string, unknown> }).data]
                  : [item];
        for (const row of data) {
            if (row && typeof row === "object") {
                const r = row as Record<string, unknown>;
                out.push({
                    implied_volatility:
                        typeof r.implied_volatility === "number"
                            ? r.implied_volatility
                            : undefined,
                    iv: typeof r.iv === "number" ? r.iv : undefined,
                    timestamp:
                        typeof r.timestamp === "string"
                            ? r.timestamp
                            : undefined,
                    ...r,
                });
            }
        }
    }
    return out;
}

/** Params for at_time endpoints: date (YYYYMMDD) and msOfDay (ms since midnight ET) or timeOfDay (hh:mm:ss.SSS). Terminal requires start_date, end_date, time_of_day. */
export interface ThetaOptionAtTimeParams {
    root: string;
    exp: string;
    strike: number;
    right: "C" | "P";
    date: string; // YYYYMMDD
    msOfDay?: number; // ms since midnight ET, e.g. 34200000 = 09:30 (converted to time_of_day)
    timeOfDay?: string; // optional hh:mm:ss.SSS ET (use if already formatted)
}

function msToTimeOfDay(ms: number): string {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = (ms % 60000) / 1000;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toFixed(3)}`;
}

/**
 * Fetch option at_time/trade (trade at a specific time). Uses start_date, end_date, time_of_day (hh:mm:ss.SSS).
 */
export async function fetchThetaOptionAtTimeTrade(
    params: ThetaOptionAtTimeParams,
): Promise<ThetaOptionSnapshotTrade | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const dateStr = params.date.replace(/-/g, "").slice(0, 8);
    q.set("start_date", dateStr);
    q.set("end_date", dateStr);
    q.set(
        "time_of_day",
        params.timeOfDay ??
            (params.msOfDay != null
                ? msToTimeOfDay(params.msOfDay)
                : "09:30:00.000"),
    );
    const url = buildThetaUrl("option/at_time/trade", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    const data =
        typeof item === "object" && item !== null && "data" in item
            ? (item as { data?: Record<string, unknown> }).data
            : item;
    if (data && typeof data === "object") {
        const d = data as Record<string, unknown>;
        const price = d.price ?? d.last ?? d.last_price;
        return {
            price:
                typeof price === "number"
                    ? price
                    : typeof price === "string"
                      ? parseFloat(price)
                      : undefined,
            size: typeof d.size === "number" ? d.size : undefined,
            timestamp:
                typeof d.timestamp === "string" ? d.timestamp : undefined,
            ...d,
        } as ThetaOptionSnapshotTrade;
    }
    return null;
}

/**
 * Fetch option at_time/quote (quote at a specific time). Uses start_date, end_date, time_of_day (hh:mm:ss.SSS).
 */
export async function fetchThetaOptionAtTimeQuote(
    params: ThetaOptionAtTimeParams,
): Promise<{
    bid?: number;
    ask?: number;
    timestamp?: string;
    [key: string]: unknown;
} | null> {
    const q = snapshotParams(
        params.root,
        params.exp,
        params.strike,
        params.right,
    );
    const dateStr = params.date.replace(/-/g, "").slice(0, 8);
    q.set("start_date", dateStr);
    q.set("end_date", dateStr);
    q.set(
        "time_of_day",
        params.timeOfDay ??
            (params.msOfDay != null
                ? msToTimeOfDay(params.msOfDay)
                : "09:30:00.000"),
    );
    const url = buildThetaUrl("option/at_time/quote", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown>>(url);
    const response = raw?.response;
    if (response == null) return null;
    const list = Array.isArray(response) ? response : [response];
    const item = list[0];
    if (item == null) return null;
    const data =
        typeof item === "object" && item !== null && "data" in item
            ? (item as { data?: Record<string, unknown> }).data
            : item;
    if (data && typeof data === "object") {
        const d = data as Record<string, unknown>;
        return {
            bid: typeof d.bid === "number" ? d.bid : undefined,
            ask: typeof d.ask === "number" ? d.ask : undefined,
            timestamp:
                typeof d.timestamp === "string" ? d.timestamp : undefined,
            ...d,
        };
    }
    return null;
}

/**
 * Fetch historical option OHLC for one contract.
 * Requires root, exp, strike, right, startDate, endDate; intervalMs recommended (e.g. 60000).
 */
export async function fetchThetaOptionOHLC(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionOHLCRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/ohlc", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(
        url,
        !!params.useCsv,
    );
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const format = raw?.header?.format || [
        "ms_of_day",
        "open",
        "high",
        "low",
        "close",
        "volume",
        "count",
        "date",
    ];
    return response.map((row: unknown) => {
        const arr = Array.isArray(row) ? row : [];
        const obj: Record<string, number> = {};
        format.forEach((key: string, i: number) => {
            obj[key] = Number(arr[i]) ?? 0;
        });
        return obj as unknown as ThetaOptionOHLCRow;
    });
}

/**
 * Fetch historical option first-order Greeks for one contract (option/history/greeks/first_order).
 * Intraday Greeks; use interval=1m via buildParams.
 */
export async function fetchThetaOptionGreeks(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionGreeksRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/first_order", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(
        url,
        !!params.useCsv,
    );
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const format = raw?.header?.format || [
        "ms_of_day",
        "delta",
        "gamma",
        "theta",
        "vega",
        "rho",
        "bid",
        "ask",
        "underlying_price",
        "date",
    ];
    return response.map((row: unknown) => {
        const arr = Array.isArray(row) ? row : [];
        const obj: Record<string, number> = {};
        format.forEach((key: string, i: number) => {
            obj[key] = Number(arr[i]) ?? 0;
        });
        return obj as unknown as ThetaOptionGreeksRow;
    });
}

/**
 * Fetch historical option second-order Greeks (gamma, vanna, charm, etc.) for one contract.
 */
function numFromUnknown(v: unknown): number {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}

/** Map API row (array or object) to second-order Greeks; tolerates header/length mismatches. */
function parseSecondOrderRow(
    row: unknown,
    format: string[],
): ThetaOptionGreeksSecondOrderRow | null {
    if (row == null) return null;
    if (typeof row === "object" && !Array.isArray(row)) {
        const o = row as Record<string, unknown>;
        const pick = (...keys: string[]) => {
            for (const k of keys) {
                if (k in o && o[k] != null) return numFromUnknown(o[k]);
            }
            return 0;
        };
        return {
            ms_of_day: pick("ms_of_day", "msOfDay"),
            bid: pick("bid"),
            ask: pick("ask"),
            gamma: pick("gamma"),
            vanna: pick("vanna"),
            charm: pick("charm"),
            vomma: pick("vomma"),
            veta: pick("veta"),
            implied_vol: pick(
                "implied_vol",
                "implied_volatility",
                "impliedVolatility",
                "iv",
            ),
            iv_error: pick("iv_error"),
            ms_of_day2: pick("ms_of_day2"),
            underlying_price: pick("underlying_price", "underlyingPrice"),
            date: pick("date"),
        };
    }
    const arr = Array.isArray(row) ? row : [];
    const obj: Record<string, number> = {};
    format.forEach((key: string, i: number) => {
        obj[key] = numFromUnknown(arr[i]);
    });
    if (!obj.date && arr.length) {
        const asDate = arr.find((x) => {
            const n = numFromUnknown(x);
            return n >= 20000101 && n <= 21001231;
        });
        if (asDate != null) obj.date = numFromUnknown(asDate);
    }
    return obj as unknown as ThetaOptionGreeksSecondOrderRow;
}

export async function fetchThetaOptionGreeksSecondOrder(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionGreeksSecondOrderRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/second_order", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(
        url,
        !!params.useCsv,
    );
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const format = raw?.header?.format || [
        "ms_of_day",
        "bid",
        "ask",
        "gamma",
        "vanna",
        "charm",
        "vomma",
        "veta",
        "implied_vol",
        "iv_error",
        "ms_of_day2",
        "underlying_price",
        "date",
    ];
    const out: ThetaOptionGreeksSecondOrderRow[] = [];
    for (const row of response) {
        const parsed = parseSecondOrderRow(row, format);
        if (parsed) out.push(parsed);
    }
    return out;
}

/**
 * Fetch historical option third-order Greeks (speed, color, ultima, etc.) for one contract.
 */
function parseThirdOrderRow(
    row: unknown,
    format: string[],
): ThetaOptionGreeksThirdOrderRow | null {
    if (row == null) return null;
    if (typeof row === "object" && !Array.isArray(row)) {
        const o = row as Record<string, unknown>;
        const pick = (...keys: string[]) => {
            for (const k of keys) {
                if (k in o && o[k] != null) return numFromUnknown(o[k]);
            }
            return 0;
        };
        return {
            ms_of_day: pick("ms_of_day", "msOfDay"),
            date: pick("date"),
            speed: pick("speed"),
            color: pick("color"),
            ultima: pick("ultima"),
        };
    }
    const arr = Array.isArray(row) ? row : [];
    const obj: Record<string, number> = {
        ms_of_day: 0,
        date: 0,
        speed: 0,
        color: 0,
        ultima: 0,
    };
    format.forEach((key: string, i: number) => {
        obj[key] = numFromUnknown(arr[i]);
    });
    if (!obj.date && arr.length) {
        const asDate = arr.find((x) => {
            const n = numFromUnknown(x);
            return n >= 20000101 && n <= 21001231;
        });
        if (asDate != null) obj.date = numFromUnknown(asDate);
    }
    return obj as unknown as ThetaOptionGreeksThirdOrderRow;
}

export async function fetchThetaOptionGreeksThirdOrder(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionGreeksThirdOrderRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/third_order", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(
        url,
        !!params.useCsv,
    );
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const format = raw?.header?.format || [
        "ms_of_day",
        "speed",
        "color",
        "ultima",
        "date",
    ];
    const out: ThetaOptionGreeksThirdOrderRow[] = [];
    for (const row of response) {
        const parsed = parseThirdOrderRow(row, format);
        if (parsed) out.push(parsed);
    }
    return out;
}

/** One row from option/history/greeks/all (combined columns; names vary by terminal version). */
export interface ThetaOptionHistoryGreeksAllRow {
    ms_of_day: number;
    date: number;
    delta?: number;
    gamma?: number;
    vanna?: number;
    charm?: number;
    vomma?: number;
    veta?: number;
    implied_vol?: number;
    speed?: number;
    color?: number;
    ultima?: number;
    underlying_price?: number;
}

function parseHistoryGreeksAllRow(
    row: unknown,
    format: string[],
): ThetaOptionHistoryGreeksAllRow | null {
    if (row == null) return null;
    if (typeof row === "object" && !Array.isArray(row)) {
        const o = row as Record<string, unknown>;
        const pick = (...keys: string[]) => {
            for (const k of keys) {
                if (k in o && o[k] != null) return numFromUnknown(o[k]);
            }
            return 0;
        };
        const d = pick("date");
        if (d < 20000101 || d > 21001231) return null;
        return {
            ms_of_day: pick("ms_of_day", "msOfDay"),
            date: d,
            delta: pick("delta"),
            gamma: pick("gamma"),
            vanna: pick("vanna"),
            charm: pick("charm"),
            vomma: pick("vomma"),
            veta: pick("veta"),
            implied_vol: pick(
                "implied_vol",
                "implied_volatility",
                "impliedVolatility",
                "iv",
            ),
            speed: pick("speed"),
            color: pick("color"),
            ultima: pick("ultima"),
            underlying_price: pick("underlying_price", "underlyingPrice"),
        };
    }
    const arr = Array.isArray(row) ? row : [];
    const obj: Record<string, number> = {};
    format.forEach((key: string, i: number) => {
        if (i < arr.length) obj[key] = numFromUnknown(arr[i]);
    });
    if (!obj.date && arr.length) {
        const asDate = arr.find((x) => {
            const n = numFromUnknown(x);
            return n >= 20000101 && n <= 21001231;
        });
        if (asDate != null) obj.date = numFromUnknown(asDate);
    }
    if (obj.date < 20000101 || obj.date > 21001231) return null;
    const ivAlt = obj["implied_volatility"];
    return {
        ms_of_day: obj.ms_of_day ?? 0,
        date: obj.date,
        delta: obj.delta,
        gamma: obj.gamma,
        vanna: obj.vanna,
        charm: obj.charm,
        vomma: obj.vomma,
        veta: obj.veta,
        implied_vol:
            obj.implied_vol ?? (typeof ivAlt === "number" ? ivAlt : undefined),
        speed: obj.speed,
        color: obj.color,
        ultima: obj.ultima,
        underlying_price: obj.underlying_price,
    };
}

/**
 * Historical intraday/EOD-style greeks in one request (replaces separate second_order + third_order calls for the same window).
 */
export async function fetchThetaOptionHistoryGreeksAll(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaOptionHistoryGreeksAllRow[]> {
    const q = buildParams({ ...params });
    const url = buildThetaUrl("option/history/greeks/all", q);
    const raw = await thetaFetch<ThetaApiResponse<unknown[]>>(
        url,
        !!params.useCsv,
    );
    const response = raw?.response;
    if (!Array.isArray(response)) return [];
    const format = raw?.header?.format || [];
    const out: ThetaOptionHistoryGreeksAllRow[] = [];
    for (const row of response) {
        const parsed = parseHistoryGreeksAllRow(row, format);
        if (parsed) out.push(parsed);
    }
    return out;
}

/** Normalized bar for charts: time (UTC sec), open, high, low, close, volume, and optional greeks. */
export interface ThetaHistoricalOptionBar {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    count?: number;
    date: number;
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    underlying_price?: number;
}

/**
 * Fetch historical intraday option data for one contract: OHLC plus first-order Greeks when available.
 * Returns bars suitable for gamma exposure / volume delta by strike over time.
 */
export async function fetchThetaHistoricalOptions(
    params: ThetaHistoricalOptionsParams & {
        strike: number;
        exp: string;
        right: "C" | "P";
    },
): Promise<ThetaHistoricalOptionBar[]> {
    const [ohlc, greeks] = await Promise.all([
        fetchThetaOptionOHLC(params),
        fetchThetaOptionGreeks(params).catch(() => []),
    ]);
    if (!ohlc.length) return [];
    const greeksByMs = new Map<number, ThetaOptionGreeksRow>();
    greeks.forEach((g) => greeksByMs.set(g.ms_of_day, g));
    const dateNum = ohlc[0]?.date ?? parseInt(params.startDate, 10);
    return ohlc.map((bar) => {
        const g = greeksByMs.get(bar.ms_of_day);
        const timeSec = dateToUtcSec(dateNum, bar.ms_of_day);
        return {
            time: timeSec,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close,
            volume: bar.volume,
            count: bar.count,
            date: bar.date,
            delta: g?.delta,
            gamma: g?.gamma,
            theta: g?.theta,
            vega: g?.vega,
            underlying_price: g?.underlying_price,
        };
    });
}

/** Convert YYYYMMDD + ms_of_day (ET midnight) to UTC seconds (for chart time). */
function dateToUtcSec(dateNum: number, msOfDay: number): number {
    const y = Math.floor(dateNum / 10000);
    const m = Math.floor((dateNum % 10000) / 100) - 1;
    const d = dateNum % 100;
    const date = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
    const etOffsetMs = 5 * 60 * 60 * 1000;
    const etMidnight = date.getTime() - etOffsetMs;
    const barMs = etMidnight + msOfDay;
    return Math.floor(barMs / 1000);
}

/**
 * Fetch historical intraday for multiple contracts (e.g. chain slice).
 * Returns a map: contractKey -> bars. contractKey = `${exp}_${strike}_${right}`.
 */
export async function fetchThetaHistoricalOptionsChain(
    params: ThetaHistoricalOptionsParams & {
        contracts: Array<{ exp: string; strike: number; right: "C" | "P" }>;
    },
): Promise<Record<string, ThetaHistoricalOptionBar[]>> {
    const out: Record<string, ThetaHistoricalOptionBar[]> = {};
    const intervalMs = params.intervalMs ?? 60000;
    const CONCURRENCY = 4;
    const queue = [...params.contracts];
    const work = async () => {
        while (queue.length > 0) {
            const c = queue.shift();
            if (!c) break;
            const key = `${c.exp}_${c.strike}_${c.right}`;
            try {
                const bars = await fetchThetaHistoricalOptions({
                    ...params,
                    exp: c.exp,
                    strike: c.strike,
                    right: c.right,
                    intervalMs,
                });
                out[key] = bars;
            } catch {
                out[key] = [];
            }
        }
    };
    await Promise.all(
        Array.from(
            { length: Math.min(CONCURRENCY, params.contracts.length) },
            () => work(),
        ),
    );
    return out;
}
